// PROMPT 14G-1 — Territory Reports export helpers.
// Client-side only: exports EXACTLY what the (already RBAC-scoped + filtered) page loaded.
// No new backend surface, no new calculations, no private/internal/revenue fields.

const csvCell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const row = (arr) => arr.map(csvCell).join(",");

const convText = (c) => (c.enough_data ? `${c.conversion_rate}%` : "Not enough data yet");

const SCORECARD_HEADERS = [
  "Territory / Franchise", "Geography path", "Publish status", "Branch ID", "Franchise ID",
  "Leads (range)", "Consultations", "Conversion rate", "Open opportunities", "Referrals sent",
  "External referrals awaiting", "Documents needing review", "Stale documents", "Local page leads",
  "Last activity",
];

const scorecardRow = (c) => row([
  c.territory_name, c.geography_path, c.publish_status, c.branch_id || "", c.franchise_id || "",
  c.leads, c.consultations, convText(c), c.open_opportunities, c.referrals_sent,
  c.external_referrals_awaiting, c.documents_needing_review, c.stale_documents, c.local_page_leads,
  c.last_activity || "",
]);

const summaryPairs = (summary) => {
  const la = summary?.lead_activity || {};
  const pipe = summary?.pipeline_activity || {};
  const lp = summary?.local_pages || {};
  const ext = summary?.external_referrals || {};
  const dm = summary?.documents_meetings || {};
  return [
    ["Total leads (all-time)", la.total_leads], ["New leads (range)", la.new_leads],
    ["Consultation requests", la.consultation_requests], ["Completed intake forms", la.completed_intake_forms],
    ["Live chat leads", la.live_chat_leads], ["Local page leads", la.local_page_leads],
    ["Reverse search leads", la.reverse_search_leads],
    ["Open opportunities", pipe.open_opportunities], ["Consultations booked", pipe.consultations_booked],
    ["Roadmaps created", pipe.roadmaps_created], ["Referrals sent", pipe.referrals_sent],
    ["Closed won", pipe.closed_won], ["Closed lost", pipe.closed_lost],
    ["Long-term follow-up", pipe.long_term_follow_up],
    ["Local service-lane page leads", lp.local_service_lane_page_leads],
    ["External referrals sent", ext.sent], ["External accepted", ext.accepted],
    ["External declined", ext.declined], ["External awaiting response", ext.awaiting_response],
    ["External overdue / stalled", ext.overdue_stalled],
    ["Documents requested", dm.documents_requested], ["Documents waiting review", dm.documents_waiting_review],
    ["Stale documents", dm.stale_documents], ["Professional reviews pending", dm.professional_reviews_pending],
    ["Meetings scheduled", dm.meetings_scheduled], ["Meetings completed", dm.meetings_completed],
  ];
};

const summaryRows = (summary) => summaryPairs(summary).map(([k, v]) => row([k, v ?? 0]));

const laneRows = (summary) => {
  const sl = summary?.service_lanes || {};
  const out = [];
  (sl.leads_by_lane || []).forEach((r) => out.push(row([r.name, "Leads", r.value])));
  (sl.opportunities_by_lane || []).forEach((r) => out.push(row([r.name, "Opportunities", r.value])));
  return out;
};

export function buildTerritoryCsv(summary, scorecards, filterLines) {
  const lines = [];
  lines.push("MovingMyMama — Territory Report (Internal planning — aggregate data only)");
  lines.push(row(["Generated", new Date().toLocaleString()]));
  (filterLines || []).forEach((f) => lines.push(row(["Filter", f])));
  lines.push("");
  lines.push("SUMMARY METRICS");
  lines.push(row(["Metric", "Value"]));
  summaryRows(summary).forEach((l) => lines.push(l));
  lines.push("");
  lines.push("SERVICE LANE BREAKDOWN");
  lines.push(row(["Service lane", "Type", "Count"]));
  laneRows(summary).forEach((l) => lines.push(l));
  lines.push("");
  lines.push("TERRITORY SCORECARDS");
  lines.push(row(SCORECARD_HEADERS));
  (scorecards?.scorecards || []).forEach((c) => lines.push(scorecardRow(c)));
  return lines.join("\n");
}

export function downloadTerritoryCsv(summary, scorecards, filterLines) {
  const csv = buildTerritoryCsv(summary, scorecards, filterLines);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `territory-report-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const esc = (v) => String(v === null || v === undefined ? "" : v)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function openTerritoryPrintReport(summary, scorecards, filterLines, scopeLabel) {
  const w = window.open("", "_blank");
  if (!w) return false;
  const sr = summaryPairs(summary).map(([k, v]) =>
    `<tr><td>${esc(k)}</td><td style="text-align:right">${esc(v ?? 0)}</td></tr>`).join("");
  const sc = (scorecards?.scorecards || []).map((c) => `
    <tr>
      <td>${esc(c.territory_name)}${c.is_legacy ? ' <span class="tag">Super Admin only</span>' : ""}<div class="muted">${esc(c.geography_path)}</div></td>
      <td>${esc(c.publish_status)}</td>
      <td style="text-align:right">${esc(c.leads)}</td>
      <td style="text-align:right">${esc(c.consultations)}</td>
      <td style="text-align:right">${esc(convText(c))}</td>
      <td style="text-align:right">${esc(c.open_opportunities)}</td>
      <td style="text-align:right">${esc(c.referrals_sent)}</td>
      <td style="text-align:right">${esc(c.external_referrals_awaiting)}</td>
      <td style="text-align:right">${esc(c.documents_needing_review)}</td>
      <td style="text-align:right">${esc(c.stale_documents)}</td>
      <td style="text-align:right">${esc(c.local_page_leads)}</td>
    </tr>`).join("");
  const filters = (filterLines || []).map((f) => `<li>${esc(f)}</li>`).join("") || "<li>No filters (all in scope)</li>";
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Territory Report — MovingMyMama</title>
  <style>
    body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#252221;padding:28px;max-width:1000px;margin:auto}
    h1{color:#1C4358;font-size:22px;margin:0 0 2px}
    .sub{color:#6B7280;font-size:13px;margin-bottom:14px}
    h2{color:#1C4358;font-size:15px;margin:22px 0 8px;border-bottom:2px solid #E6EDF2;padding-bottom:4px}
    ul{margin:4px 0 0;padding-left:18px;color:#4B5563;font-size:12px}
    table{width:100%;border-collapse:collapse;font-size:12px}
    th,td{border:1px solid #E6EDF2;padding:6px 8px;text-align:left;vertical-align:top}
    th{background:#F4F7F9;color:#1C4358;font-weight:700}
    .muted{color:#9AA5B1;font-size:11px;margin-top:2px}
    .tag{background:rgba(156,30,34,.1);color:#9C1E22;font-size:10px;padding:1px 6px;border-radius:10px}
    .disclaimer{margin-top:22px;font-size:11px;color:#6B7280;border-top:1px solid #E6EDF2;padding-top:10px}
    @media print{body{padding:0}}
  </style></head><body>
    <h1>MovingMyMama — Territory Report</h1>
    <div class="sub">${esc(scopeLabel || "")} · Generated ${esc(new Date().toLocaleString())}</div>
    <h2>Filters applied</h2>
    <ul>${filters}</ul>
    <h2>Summary metrics</h2>
    <table><thead><tr><th>Metric</th><th style="text-align:right">Value</th></tr></thead><tbody>${sr}</tbody></table>
    <h2>Territory scorecards</h2>
    <table><thead><tr>
      <th>Territory / Geography</th><th>Status</th><th>Leads</th><th>Consults</th><th>Conversion</th>
      <th>Open opps</th><th>Referrals</th><th>Partner awaiting</th><th>Docs review</th><th>Stale docs</th><th>Local leads</th>
    </tr></thead><tbody>${sc || '<tr><td colspan="11">No territories in scope.</td></tr>'}</tbody></table>
    <div class="disclaimer">Internal planning report — aggregate data only.</div>
    <script>window.onload=function(){setTimeout(function(){window.print();},350);}<\/script>
  </body></html>`);
  w.document.close();
  return true;
}
